const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const formatDdMm = (day: number, month: number): string =>
  `${day.toString().padStart(2, "0")}-${month.toString().padStart(2, "0")}`;

export const parseDdMm = (date: string): { day: number; month: number } => {
  const [day, month] = date.split("-").map(Number);
  return { day, month };
};

export const getDaysInMonth = (month: number): number =>
  DAYS_IN_MONTH[month - 1];

const addDaysInRange = (
  dates: string[],
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number,
) => {
  const year = 2024;
  const current = new Date(year, startMonth - 1, startDay);
  const end = new Date(year, endMonth - 1, endDay);

  while (current <= end) {
    dates.push(formatDdMm(current.getDate(), current.getMonth() + 1));
    current.setDate(current.getDate() + 1);
  }
};

export const getDatesInPeriod = (
  fromDdMm: string,
  toDdMm: string,
): string[] => {
  const { day: fromDay, month: fromMonth } = parseDdMm(fromDdMm);
  const { day: toDay, month: toMonth } = parseDdMm(toDdMm);
  const dates: string[] = [];

  const fromValue = fromMonth * 100 + fromDay;
  const toValue = toMonth * 100 + toDay;

  if (fromValue <= toValue) {
    addDaysInRange(dates, fromMonth, fromDay, toMonth, toDay);
  } else {
    addDaysInRange(dates, fromMonth, fromDay, 12, 31);
    addDaysInRange(dates, 1, 1, toMonth, toDay);
  }

  return dates;
};

export const formatPeriodLabel = (fromDdMm: string, toDdMm: string): string => {
  const formatSingle = (ddMm: string) => {
    const { day, month } = parseDdMm(ddMm);
    const monthName = MONTHS_LONG[month - 1];
    return `${day.toString().padStart(2, "0")}. ${monthName}`;
  };

  return `${formatSingle(fromDdMm)} – ${formatSingle(toDdMm)}`;
};

/** Short label for the badge, e.g. «1. nov – 30. nov». */
export const formatShortPeriodLabel = (
  fromDdMm: string,
  toDdMm: string,
): string => {
  const formatSingle = (ddMm: string) => {
    const { day, month } = parseDdMm(ddMm);
    return `${day}. ${MONTHS_SHORT[month - 1].toLowerCase()}`;
  };
  return `${formatSingle(fromDdMm)} – ${formatSingle(toDdMm)}`;
};

export const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

export const MONTHS_LONG = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

/** «15:04» + 7 → «15:11» (over midnatt går rundt). Ugyldig tid gir null. */
export function addMinutesToTime(time: string, minutes: number): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time ?? "");
  if (!m) return null;
  const total = (((Number(m[1]) * 60 + Number(m[2]) + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export type JamatPrayer = "fajr" | "duhr" | "asr" | "maghrib" | "isha";

/**
 * Bønnetiden (salah) offseten regnes fra. Backend sender jamat-tiden ferdig utregnet for bønner med
 * offset, så salah = utregnet tid − offset. Brukes til å vise tiden appene og TV-ene viser.
 */
export function salahBaseFrom(times: Record<string, unknown>): Partial<Record<JamatPrayer, string>> {
  const base: Partial<Record<JamatPrayer, string>> = {};
  (["fajr", "duhr", "asr", "maghrib", "isha"] as JamatPrayer[]).forEach((p) => {
    const offset = times[`${p}JamatOffset`];
    const time = times[p];
    if (typeof offset === "number" && offset >= 0 && typeof time === "string") {
      const salah = addMinutesToTime(time, -offset);
      if (salah) base[p] = salah;
    }
  });
  return base;
}
